import { assertNever } from "@msys/common";
import { z } from "zod";
import type { CreateableOrganisationType } from "../isValidOrganisationType.js";

/**
 * The signup claims of the ID token: what the Keycloak registration collected
 * (`signup_as`, `company_name`, emitted only when the `signup` scope was
 * requested) plus the name Keycloak stores for every user.
 *
 * The claims are untrusted user input, exactly like the value of the
 * organisation-creation form: the backend validation of the title applies
 * unchanged. A token that does not match is simply not a signup token.
 */

/** Same limit as the realm's user-profile validator for `companyName`. */
const MAX_COMPANY_NAME_LENGTH = 200;

/** A name part that is not a usable string is treated as absent; it only feeds a fallback. */
const optionalName = z.string().trim().optional().catch(undefined);

const signupClaimsSchema = z.object({
  signup_as: z.enum(["CRAFTSMAN", "CLIENT"]),
  company_name: z.string().trim().max(MAX_COMPANY_NAME_LENGTH).optional(),
  given_name: optionalName,
  family_name: optionalName,
});

export interface SignupClaims {
  organisationType: CreateableOrganisationType;
  /** Trimmed; `undefined` when the claim is absent or blank. */
  companyName: string | undefined;
  /** The user's name from the token; `undefined` when the token carries none. */
  fullName: string | undefined;
}

/**
 * Parses the signup claims out of the parsed ID token. Returns `null` when
 * `signup_as` is absent or not `CRAFTSMAN`/`CLIENT`, or when `company_name`
 * is not a string of at most 200 characters.
 */
export function parseSignupClaims(idTokenParsed: unknown): SignupClaims | null {
  const parsed = signupClaimsSchema.safeParse(idTokenParsed);
  if (!parsed.success) {
    return null;
  }

  const { signup_as, company_name, given_name, family_name } = parsed.data;
  return {
    organisationType: signup_as,
    companyName: company_name || undefined,
    fullName: [given_name, family_name].filter(Boolean).join(" ") || undefined,
  };
}

/**
 * The organisation title the signup claims stand for, or `null` when an
 * organisation must not be created without asking: a craftsman organisation is
 * named after the company, so it needs one; a homeowner organisation is named
 * after the person, since the registration asks a property owner for no name
 * and the setup's "Your property" step names the property.
 */
export function deriveSignupOrganisationTitle(
  claims: SignupClaims
): string | null {
  switch (claims.organisationType) {
    case "CRAFTSMAN":
      return claims.companyName ?? null;
    case "CLIENT":
      return claims.fullName ?? null;
    default:
      return assertNever(claims.organisationType);
  }
}
